/** Singapore NRIC / passport format: one of STFGM + 7 digits + checksum letter. */
export const NRIC_PASSPORT_REGEX = /^[STFGM]\d{7}[A-Z]$/;

const NRIC_MAX_LENGTH = 9;
const NRIC_VISIBLE_TAIL_LENGTH = 4;

/** Alphanumeric passport / document id for display masking (non-SG NRIC shapes). */
const normalizeDisplayDocument = (value) => (
	String(value ?? "")
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, "")
);

const maskDocumentTail = (strValue) => {
	if (!strValue) return "";
	if (strValue.length <= NRIC_VISIBLE_TAIL_LENGTH) {
		return "*".repeat(strValue.length);
	}
	return (
		"*".repeat(strValue.length - NRIC_VISIBLE_TAIL_LENGTH) + strValue.slice(-NRIC_VISIBLE_TAIL_LENGTH)
	);
};

/**
 * Mask all but the last 4 characters (e.g. S1234567H → *****567H).
 * If length <= 4, mask the entire string.
 * Pre-masked API values (e.g. *****987U) are shown as-is.
 */
export const maskNricOrPassport = (value) => {
	if (value === "N/A" || value === "-") return value;

	const raw = String(value ?? "").trim();
	if (!raw) return "";

	// Guest-checkin API may already return a masked passport/NRIC.
	if (raw.includes("*")) return raw;

	const nricValue = sanitizeNricInput(raw);
	const strValue = nricValue || normalizeDisplayDocument(raw);
	return maskDocumentTail(strValue);
};

/** Enforce NRIC shape while typing: [STFGM] + 7 digits + [A-Z], max 9 chars. */
export const sanitizeNricInput = (value) => {
	const upper = String(value ?? "")
		.toUpperCase()
		.replace(/[^STFGM0-9A-Z]/g, "");
	if (!upper) return "";

	const chars = [];
	for (let i = 0; i < upper.length && chars.length < NRIC_MAX_LENGTH; i += 1) {
		const ch = upper[i];
		const pos = chars.length;
		if (pos === 0) {
			if (/[STFGM]/.test(ch)) chars.push(ch);
		} else if (pos >= 1 && pos <= 7) {
			if (/\d/.test(ch)) chars.push(ch);
		} else if (pos === 8) {
			if (/[A-Z]/.test(ch)) chars.push(ch);
		}
	}
	return chars.join("");
};

/** Keep raw NRIC in form state while the input shows maskNricOrPassport(raw). */
export const mergeNricMaskedInput = (prevRaw, incoming) => {
	const raw = sanitizeNricInput(prevRaw);
	const inc = String(incoming ?? "").toUpperCase();
	const pm = maskNricOrPassport(raw);

	if (inc === pm) return raw;
	if (inc === "") return "";

	const asteriskCount = (inc.match(/\*/g) || []).length;

	if (asteriskCount === 0) {
		if (pm === "" || inc.startsWith(pm)) {
			return sanitizeNricInput(raw + inc.slice(pm.length));
		}
		return sanitizeNricInput(inc.trim());
	}

	if (inc.length < pm.length) {
		return sanitizeNricInput(raw.slice(0, Math.max(0, raw.length - 1)));
	}

	if (inc.length > pm.length) {
		return sanitizeNricInput(raw + inc.slice(pm.length).replace(/\*/g, ""));
	}

	return raw;
};

/**
 * Validate NRIC/passport for all clients (not Singapore nationality only).
 * @param {string} value
 * @param {{ required?: boolean }} options
 */
export const validateNricOrPassport = (value, { required = true } = {}) => {
	const trimmed = sanitizeNricInput(value);

	if (!trimmed) {
		if (required) {
			return { valid: false, error: "NRIC is required." };
		}
		return { valid: true, error: null };
	}

	if (!NRIC_PASSPORT_REGEX.test(trimmed)) {
		return { valid: false, error: "Invalid NRIC or passport number." };
	}

	return { valid: true, error: null };
};
